<script setup>
    import { defineExpose, defineEmits, ref, inject, reactive, computed, getCurrentInstance } from 'vue';
    import { OrganizationInfoRequest } from '@/models/management/managementModel';
    import { useManagementStore } from '@/stores/management/managementStore';
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 利用者の管理ストア
    const managementStore = useManagementStore();
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // 詳細リクエスト項目
    const organizationInfoRequest = ref({});
    // 組織セレクトボックス内容リスト
    let organizationIssue = reactive([]);
    // 組織:選択した組織情報
    //const selectOrganization = ref();
    // 組織:「選択してください」表示フラグ
    //const selectFlg = ref(false);
    // 組織:第2階層PK
    //const layer2Pk = ref('');
    // 組織:第3階層PK
    //const layer3Pk = ref('');

    // 入力欄活性コントロール
    // 基本情報
    const disabled = computed(() => {
        return organizationInfoRequest.value.TouDataFlag;
    });

    // ID入力欄の活性/非活性の状態を制御する。初期値は活性で入力可能。
    const idInputDisabled = ref(false);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 初期処理 親画面から利用可能関数
     */
    async function setInfo(OrganizationPk) {
        // 初期化する。
        idInputDisabled.value = false;

        // 詳細リクエスト項目の初期化
        organizationInfoRequest.value = new OrganizationInfoRequest();
        // 組織セレクトボックス関連情報の初期化
        organizationIssue = reactive([]);
        managementStore.organizationSelectInfo = reactive({});
        managementStore.organizationIssue = reactive([]);
        if (OrganizationPk) {
            // 更新モードの場合。

            organizationInfoRequest.value.OrganizationPk = OrganizationPk;

            // 詳細取得
            await getDetail(organizationInfoRequest.value.OrganizationPk);

            // APIから取得した契約割り当てが未設定の場合は、nullもしくは空配列が返却されるので、モデルクラス上は空配列で設定する。
            if (Utils.isNullEmptyArray(organizationInfoRequest.value.CmpContPks)) {
                organizationInfoRequest.value.CmpContPks = [];
            }

            // 編集モードの場合は、IDの入力欄は非活性にする。
            idInputDisabled.value = true;

            await getOrganizationList();
        } else {
            // 新規登録モードの場合は、親組織のプルダウンを第1階層のみに設定する。

            // 初期化する。
            managementStore.organizationIssue = [];
            organizationIssue.splice(0);

            // 初期選択用のプルダウン選択肢を生成する。
            const option = {
                pk: '',
                name: i18n.global.t('ManagementOrganizationDetail.parts.dl7'),
            };
            organizationIssue.push(option);

            // 選択してくださいを初期値に設定する。
            organizationInfoRequest.value.ParentOrganizationPk = '';

            // 組織選択APIのリクエストボディを生成する。
            // 条件は第1階層のみの選択肢を取得したいので、何もパラメーターを設定しない。
            const queryParam = {};

            // 組織選択APIをコールする。
            const response = await managementStore.getOrgSelAction(API_URL, queryParam);
            if (response.success) {
                // プルダウン選択肢を生成できるデータが取得できたか判定する。
                if (!Utils.isNullEmptyArray(managementStore.organizationIssue)) {
                    // 第1階層の取得結果分だけループする。
                    for (const org of managementStore.organizationIssue) {
                        // 第1階層に属する組織の選択肢を生成する。
                        const option = {
                            pk: org.pk,
                            name: org.name,
                        };
                        // プルダウン選択肢に格納する。
                        organizationIssue.push(option);
                    }
                }
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        }
        /*
        if (organizationInfoRequest.value.ParentOrganizationPk) {
            // 組織保存済み利用者組織セレクトボックス内容取得
            await getSeveOrganizationIssue();
        } else {
            // 組織セレクトボックス内容取得
            await getOrganizationIssue();
        }
        */
        // 企業契約情報取得
        await getCintractList();

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalMasterDetail .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }
    defineExpose({
        setInfo,
    });

    /**
     * 詳細情報処理
     */
    async function getDetail(OrganizationPk) {
        const queryParam = {
            OrganizationPk: OrganizationPk,
        };
        const response = await managementStore.getOrgDtlAction(API_URL, queryParam);
        if (response.success) {
            organizationInfoRequest.value = Object.assign(organizationInfoRequest.value, managementStore.organizationInfo);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 組織選択処理
     * @param OrganizationPk 組織PK
     */
    /*
    async function getOrganizationSelect(OrganizationPk) {
        managementStore.organizationIssue = [];
        const queryParam = {
            OrganizationPk: OrganizationPk,
        };
        const response = await managementStore.getOrgSelAction(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }
    */

    /**
     * 登録or更新処理
     */
    async function createDetail() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        //const requestBody = organizationInfoRequest.value;

        let requestBody = organizationInfoRequest.value;

        // 表示順が設定されていない場合はnullを設定する。
        if (Utils.isNullEmpty(requestBody.DisplayOrder)) {
            requestBody.DisplayOrder = null;
        }

        // 契約割り当てが選択されていない場合はnullを設定する。
        if (Utils.isNullEmptyArray(requestBody.CmpContPks)) {
            requestBody.CmpContPks = null;
        }

        if (organizationInfoRequest.value.OrganizationPk === '') {
            const response = await managementStore.postOrgDtlAction(API_URL, requestBody);
            if (response.success) {
                closeAndUpdate();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            const response = await managementStore.putOrgDtlAction(API_URL, requestBody);
            if (response.success) {
                closeAndUpdate();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';
        // ID
        if (Utils.isNullEmpty(organizationInfoRequest.value.OrganizationId)) {
            message += i18n.global.t('ManagementOrganizationDetail.message.message1') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfUserId(organizationInfoRequest.value.OrganizationId)) {
                message += i18n.global.t('ManagementOrganizationDetail.message.message2') + '\n';
            }
            // 入力可能文字のチェック
            if (!Utils.isMatchPatternAlphabetNumberSymbolForManagementId(organizationInfoRequest.value.OrganizationId)) {
                message += i18n.global.t('ManagementOrganizationDetail.message.message7') + '\n';
            }
        }
        // 日本語名
        if (Utils.isNullEmpty(organizationInfoRequest.value.OrganizationNmJ)) {
            message += i18n.global.t('ManagementOrganizationDetail.message.message3') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfManagementLongNameString(organizationInfoRequest.value.OrganizationNmJ)) {
                message += i18n.global.t('ManagementOrganizationDetail.message.message4') + '\n';
            }
        }
        // 英語名
        if (!Utils.isNullEmpty(organizationInfoRequest.value.OrganizationNmE)) {
            // 文字数
            if (Utils.checkLengthOfManagementLongNameString(organizationInfoRequest.value.OrganizationNmE)) {
                message += i18n.global.t('ManagementOrganizationDetail.message.message5') + '\n';
            }
            // 入力可能文字のチェック
            if (!Utils.isMatchPatternAlphabetNumberSymbolForManagementNameEn(organizationInfoRequest.value.OrganizationNmE)) {
                message += i18n.global.t('ManagementOrganizationDetail.message.message8') + '\n';
            }
        }
        // 表示順
        if (!Utils.isNullEmpty(organizationInfoRequest.value.DisplayOrder)) {
            // 文字数
            if (Utils.checkLengthOfDisplayOrderNumber(organizationInfoRequest.value.DisplayOrder)) {
                message += i18n.global.t('ManagementOrganizationDetail.message.message6') + '\n';
            }
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * 組織取得処理 セレクトボックス用
     * @param OrganizationPk 組織PK
     */
    /*
    async function getOrganizationInfo(OrganizationPk) {
        const queryParam = {
            OrganizationPk: OrganizationPk,
        };
        const response = await managementStore.getOrgDtlAction2(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }
    */
    /**
     * 組織セレクトボックス取得処理
     */
    async function getOrganizationList() {
        // Storeを初期化する。
        managementStore.organizationIssue = [];
        managementStore.organizationInfo = {};
        organizationIssue.splice(0);

        // 現在の親組織IDの座標を初期化する。
        // 親組織のプルダウンの内容に応じて、座標を制御する。
        let parentLevel = 0;

        // 親組織プルダウンが変更されて、現在の親組織のPK情報が取得できたか判定する。
        if (!Utils.isNullEmpty(organizationInfoRequest.value.ParentOrganizationPk)) {
            // 親組織の組織名を取得するために、親組織PKをキーに、企業組織取得APIをコールする。
            //await getOrganizationInfo(organizationInfoRequest.value.OrganizationPk);

            const queryParam = {
                OrganizationPk: organizationInfoRequest.value.ParentOrganizationPk,
            };
            const response = await managementStore.getOrgDtlForParentAction(API_URL, queryParam);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }

            // 取得した親組織の階層レベルを格納する。
            parentLevel = managementStore.organizationParentInfo.OrganizationLv;
        }

        // 現在の座標が取得できなかった場合。つまり、第1階層まで戻ってきた場合。
        if (parentLevel === 0) {
            // 第1階層のルート。

            // 初期値は選択してくださいを設定する。
            const defalutOption = {
                pk: '',
                name: i18n.global.t('ManagementOrganizationDetail.parts.dl7'),
            };
            organizationIssue.push(defalutOption);

            // 選択してくださいを初期値に設定する。
            organizationInfoRequest.value.ParentOrganizationPk = '';

            // 第1階層の組織データをプルダウンに生成するためのリクエストボディ条件を制御する。
            const requestBody = {};

            // 企業組織選択APIをコールする。
            const response = await managementStore.getOrgSelAction(API_URL, requestBody);

            if (response.success) {
                if (!Utils.isNullEmptyArray(managementStore.organizationIssue)) {
                    for (const org of managementStore.organizationIssue) {
                        // 第1階層に属する組織の選択肢を生成する。
                        const option = {
                            pk: org.pk,
                            name: org.name,
                        };
                        organizationIssue.push(option);
                    }
                }
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            // 第1階層以外の場合。

            // 上の階層に戻るの選択肢。
            // 自分の親の親階層に戻る。
            const backToParentOrgOption = {
                pk: Utils.changeNullValueToEmpty(managementStore.organizationParentInfo.ParentOrganizationPk),
                name: i18n.global.t('ManagementOrganizationDetail.parts.dl9'),
            };
            organizationIssue.push(backToParentOrgOption);

            // 自分の親階層の情報を設定する。
            const defalutOption = {
                pk: organizationInfoRequest.value.ParentOrganizationPk,
                name: managementStore.organizationParentInfo.OrganizationNmJ,
            };
            organizationIssue.push(defalutOption);

            // 企業組織選択APIのクエリパラメーターを生成する。
            // 座標は、自分自身の組織PKを設定する。
            const queryParam = {
                OrganizationPk: organizationInfoRequest.value.ParentOrganizationPk,
            };

            // 企業組織選択APIをコールする。
            const response2 = await managementStore.getOrgSelAction(API_URL, queryParam);
            if (response2.success) {
                if (!Utils.isNullEmptyArray(managementStore.organizationIssue)) {
                    // 更新モードかどうかを判定する。
                    if (!Utils.isNullEmpty(organizationInfoRequest.value.OrganizationPk)) {
                        // 更新モードの場合は、自分自身の組織の選択肢を親組織のプルダウンの一覧から除外する。
                        // 自分自身を除外したことで、自分の配下の組織も選択できないので、結果的に表示されない。
                        for (const org of managementStore.organizationIssue) {
                            if (org.pk !== organizationInfoRequest.value.OrganizationPk) {
                                const option = {
                                    pk: org.pk,
                                    name: '　' + org.name,
                                };
                                organizationIssue.push(option);
                            }
                        }
                    } else {
                        // 自分自身の組織PKで無ければ、全部の自階層と同一の組織をプルダウン選択肢に生成する。
                        for (const org of managementStore.organizationIssue) {
                            const option = {
                                pk: org.pk,
                                name: '　' + org.name,
                            };
                            organizationIssue.push(option);
                        }
                    }
                }
            } else {
                messageDialog('error', response2.errorMessage);
                if (response2.redirect) {
                    location.href = response2.redirectUrl;
                }
                return;
            }
        }
    }
    /**
     * 組織保存済み利用者組織セレクトボックス取得処理
     */
    /*
    async function getSeveOrganizationIssue() {
        await getOrganizationInfo(organizationInfoRequest.value.ParentOrganizationPk);
        if (!Utils.isNullEmpty(managementStore.organizationSelectInfo)) {
            // 各階層の戻りPKをセット
            if (!Utils.isNullEmpty(managementStore.organizationSelectInfo.OrganizationL1Pk)) {
                layer2Pk.value = managementStore.organizationSelectInfo.OrganizationL1Pk;
            }
            if (!Utils.isNullEmpty(managementStore.organizationSelectInfo.OrganizationL2Pk)) {
                layer3Pk.value = managementStore.organizationSelectInfo.OrganizationL2Pk;
            }
            if (Utils.isNullEmpty(managementStore.organizationSelectInfo.OrganizationL4Pk)) {
                await getOrganizationSelect(managementStore.organizationSelectInfo.ParentOrganizationPk);
                if (managementStore.organizationIssue.length !== 0) {
                    selectOrganization.value = managementStore.organizationIssue.find(({ pk }) => pk == organizationInfoRequest.value.ParentOrganizationPk);
                    if (!Utils.isNullEmpty(selectOrganization.value)) {
                        await getOrganizationSelect(organizationInfoRequest.value.ParentOrganizationPk);
                        selectFlg.value = false;
                        if (managementStore.organizationIssue.length !== 0) {
                            organizationIssue.splice(0);
                            organizationIssue = managementStore.organizationIssue;
                            for (let i = 0; i < organizationIssue.length; i++) {
                                organizationIssue[i].name = '' + organizationIssue[i].name;
                            }
                            selectOrganization.value.name = selectOrganization.value.name.replace('', '');

                            organizationIssue.unshift(selectOrganization.value);
                            organizationIssue.unshift({
                                pk: selectOrganization.value.ParentOrgPk,
                                id: '',
                                name: i18n.global.t('ManagementOrganizationDetail.parts.dl9'),
                                OrganizationLv: 0,
                            });
                        }
                    }
                }
            } else {
                selectFlg.value = false;
                await getOrganizationSelect(managementStore.organizationSelectInfo.OrganizationL3Pk);
                if (managementStore.organizationIssue.length !== 0) {
                    organizationIssue.splice(0);
                    organizationIssue = managementStore.organizationIssue;
                    organizationIssue.unshift({
                        pk: layer3Pk.value,
                        id: '',
                        name: i18n.global.t('ManagementOrganizationDetail.parts.dl9'),
                        OrganizationLv: 0,
                    });
                }
            }
        }
    }
    */

    /**
     * 企業契約情報取得処理
     */
    async function getCintractList() {
        const queryParam = {};
        const response = await managementStore.getCmpConAction(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 親画面で実装必須関数
     */
    const emit = defineEmits(['detailModal:cancel', 'detailModal:closeAndUpdate']);
    function cancel() {
        // 親画面のキャンセル処理を呼び出す
        emit('detailModal:cancel');
    }
    function closeAndUpdate() {
        // 親画面の、モーダルを閉じて、一覧画面の再描画を行う処理を呼び出す。
        emit('detailModal:closeAndUpdate');
    }
</script>

<template>
    <section id="overlay" :style="{ display: 'block' }">
        <article id="modalMasterDetail" class="modal" style="display: block" :style="{ display: 'block' }">
            <h3 class="textCenter">{{ $t('ManagementOrganizationDetail.parts.pageTitle1') }}</h3>
            <section class="modalContents" style="height: calc(100% - 145px)">
                <section class="form dtWidth170">
                    <h4>{{ $t('ManagementOrganizationDetail.parts.pageTitle2') }}</h4>
                    <dl>
                        <!--組織ID-->
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementOrganizationDetail.parts.dl2') }} </label>
                                </li>
                                <li class="must">
                                    <span>{{ $t('ManagementOrganizationDetail.parts.dl1') }} </span>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li id="orgInputId">
                                    <input type="text" v-model="organizationInfoRequest.OrganizationId" :disabled="idInputDisabled" />
                                </li>
                            </ul>
                        </dd>
                    </dl>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementOrganizationDetail.parts.dl3') }} </label>
                                </li>
                                <li class="must">
                                    <span>{{ $t('ManagementOrganizationDetail.parts.dl1') }} </span>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li id="orgInputNameJp">
                                    <input type="text" v-model="organizationInfoRequest.OrganizationNmJ" :disabled="disabled" />
                                </li>
                            </ul>
                        </dd>
                    </dl>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementOrganizationDetail.parts.dl4') }} </label>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li id="orgInputNameEn">
                                    <input type="text" v-model="organizationInfoRequest.OrganizationNmE" :disabled="disabled" />
                                </li>
                            </ul>
                        </dd>
                    </dl>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementOrganizationDetail.parts.dl5') }} </label>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li id="orgInputOrder">
                                    <input type="number" v-model="organizationInfoRequest.DisplayOrder" :disabled="disabled" />
                                </li>
                            </ul>
                        </dd>
                    </dl>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementOrganizationDetail.parts.dl6') }}</label>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li>
                                    <div class="select">
                                        <select id="changeService" v-model="organizationInfoRequest.ParentOrganizationPk" @change="getOrganizationList" :disabled="disabled" required>
                                            <option v-for="organization in organizationIssue" :key="organization.pk" :value="organization.pk">
                                                {{ organization.name }}
                                            </option>
                                        </select>
                                    </div>
                                </li>
                            </ul>
                        </dd>
                    </dl>
                    <h4 style="margin-top: 40px">{{ $t('ManagementOrganizationDetail.parts.pageTitle3') }}</h4>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementOrganizationDetail.parts.dl8') }} </label>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <section class="innerTable">
                                <table class="table">
                                    <tbody>
                                        <tr v-for="(data, index) in managementStore.contractList" :key="index">
                                            <td style="width: 20px">
                                                <input type="checkbox" :id="'data5-' + (index + 1)" class="checkbox" v-model="organizationInfoRequest.CmpContPks" :value="data.CmpContPk" :disabled="data.TouDataFlag" />
                                                <label class="checkbox" :for="'data5-' + (index + 1)" />
                                            </td>
                                            <td style="width: auto">
                                                <span>{{ index + 1 }}. {{ data.CmpContNm }} ({{ data.CmpContPeriodS }} - {{ data.CmpContPeriodE }})</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>
                        </dd>
                    </dl>
                </section>
            </section>
            <section class="modalFooter">
                <ul class="btnArea center">
                    <li>
                        <button type="button" class="btnNormal closeModal" @click="cancel()">{{ $t('ManagementOrganizationDetail.parts.button1') }}</button>
                    </li>
                    <li>
                        <button type="button" class="btnPrimary closeModal" @click="createDetail()" :disabled="disabledStore.isDisabled">{{ $t('ManagementOrganizationDetail.parts.button2') }}</button>
                    </li>
                </ul>
            </section>
        </article>
    </section>
</template>

<style></style>
